/* eslint-disable import/no-named-as-default, import/no-named-as-default-member */
import { normalizeSiteLanguage } from '@yoyoclub/shared';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import enActivities from './locales/en/activities.json';
import enActivity from './locales/en/activity.json';
import enApplyHost from './locales/en/applyHost.json';
import enAuth from './locales/en/auth.json';
import enBooking from './locales/en/booking.json';
import enCommon from './locales/en/common.json';
import enContact from './locales/en/contact.json';
import enCourse from './locales/en/course.json';
import enDashboard from './locales/en/dashboard.json';
import enGiftCard from './locales/en/giftCard.json';
import enHome from './locales/en/home.json';
import enHostFollow from './locales/en/hostFollow.json';
import enInvestors from './locales/en/investors.json';
import enPass from './locales/en/pass.json';
import enPassRefund from './locales/en/passRefund.json';
import enPicks from './locales/en/picks.json';
import enProfiles from './locales/en/profiles.json';
import enRefund from './locales/en/refund.json';
import enReview from './locales/en/review.json';
import enRewards from './locales/en/rewards.json';
import enTerms from './locales/en/terms.json';
import enTrainingProgram from './locales/en/trainingProgram.json';
import enWaitlist from './locales/en/waitlist.json';
import zhCNActivities from './locales/zh-CN/activities.json';
import zhCNActivity from './locales/zh-CN/activity.json';
import zhCNApplyHost from './locales/zh-CN/applyHost.json';
import zhCNAuth from './locales/zh-CN/auth.json';
import zhCNBooking from './locales/zh-CN/booking.json';
import zhCNCommon from './locales/zh-CN/common.json';
import zhCNContact from './locales/zh-CN/contact.json';
import zhCNCourse from './locales/zh-CN/course.json';
import zhCNDashboard from './locales/zh-CN/dashboard.json';
import zhCNGiftCard from './locales/zh-CN/giftCard.json';
import zhCNHome from './locales/zh-CN/home.json';
import zhCNHostFollow from './locales/zh-CN/hostFollow.json';
import zhCNInvestors from './locales/zh-CN/investors.json';
import zhCNPass from './locales/zh-CN/pass.json';
import zhCNPassRefund from './locales/zh-CN/passRefund.json';
import zhCNPicks from './locales/zh-CN/picks.json';
import zhCNProfiles from './locales/zh-CN/profiles.json';
import zhCNRefund from './locales/zh-CN/refund.json';
import zhCNReview from './locales/zh-CN/review.json';
import zhCNRewards from './locales/zh-CN/rewards.json';
import zhCNTrainingProgram from './locales/zh-CN/trainingProgram.json';
import zhCNWaitlist from './locales/zh-CN/waitlist.json';
import zhTWActivities from './locales/zh-TW/activities.json';
import zhTWActivity from './locales/zh-TW/activity.json';
import zhTWApplyHost from './locales/zh-TW/applyHost.json';
import zhTWAuth from './locales/zh-TW/auth.json';
import zhTWBooking from './locales/zh-TW/booking.json';
import zhTWCommon from './locales/zh-TW/common.json';
import zhTWContact from './locales/zh-TW/contact.json';
import zhTWCourse from './locales/zh-TW/course.json';
import zhTWDashboard from './locales/zh-TW/dashboard.json';
import zhTWGiftCard from './locales/zh-TW/giftCard.json';
import zhTWHome from './locales/zh-TW/home.json';
import zhTWHostFollow from './locales/zh-TW/hostFollow.json';
import zhTWInvestors from './locales/zh-TW/investors.json';
import zhTWPass from './locales/zh-TW/pass.json';
import zhTWPassRefund from './locales/zh-TW/passRefund.json';
import zhTWPicks from './locales/zh-TW/picks.json';
import zhTWProfiles from './locales/zh-TW/profiles.json';
import zhTWRefund from './locales/zh-TW/refund.json';
import zhTWReview from './locales/zh-TW/review.json';
import zhTWRewards from './locales/zh-TW/rewards.json';
import zhTWTrainingProgram from './locales/zh-TW/trainingProgram.json';
import zhTWWaitlist from './locales/zh-TW/waitlist.json';
import { loadScript, onScriptLoaded } from './script';

// Define supported languages
export const LANGUAGES = {
  EN: 'en',
  ZH_CN: 'zh-CN',
  ZH_TW: 'zh-TW',
} as const;

export type Language = (typeof LANGUAGES)[keyof typeof LANGUAGES];

// Short names for the switcher button
export const LANGUAGE_NAMES: Record<Language, string> = {
  [LANGUAGES.EN]: 'EN',
  [LANGUAGES.ZH_CN]: '简体',
  [LANGUAGES.ZH_TW]: '繁體',
};

// Full names for the switcher menu, each written in its own language
export const LANGUAGE_FULL_NAMES: Record<Language, string> = {
  [LANGUAGES.EN]: 'English',
  [LANGUAGES.ZH_CN]: '简体中文',
  [LANGUAGES.ZH_TW]: '繁體中文',
};

// Configure i18n
i18n
  .use(LanguageDetector) // Auto-detect user language
  .use(initReactI18next) // Pass i18n instance to react-i18next
  .init({
    resources: {
      en: {
        common: enCommon,
        home: enHome,
        activities: enActivities,
        activity: enActivity,
        course: enCourse,
        dashboard: enDashboard,
        profiles: enProfiles,
        contact: enContact,
        passRefund: enPassRefund,
        refund: enRefund,
        terms: enTerms,
        applyHost: enApplyHost,
        trainingProgram: enTrainingProgram,
        auth: enAuth,
        booking: enBooking,
        pass: enPass,
        giftCard: enGiftCard,
        review: enReview,
        rewards: enRewards,
        hostFollow: enHostFollow,
        picks: enPicks,
        investors: enInvestors,
        waitlist: enWaitlist,
      },
      'zh-CN': {
        common: zhCNCommon,
        home: zhCNHome,
        activities: zhCNActivities,
        activity: zhCNActivity,
        course: zhCNCourse,
        dashboard: zhCNDashboard,
        profiles: zhCNProfiles,
        contact: zhCNContact,
        passRefund: zhCNPassRefund,
        refund: zhCNRefund,
        applyHost: zhCNApplyHost,
        trainingProgram: zhCNTrainingProgram,
        auth: zhCNAuth,
        booking: zhCNBooking,
        pass: zhCNPass,
        giftCard: zhCNGiftCard,
        review: zhCNReview,
        rewards: zhCNRewards,
        hostFollow: zhCNHostFollow,
        picks: zhCNPicks,
        investors: zhCNInvestors,
        waitlist: zhCNWaitlist,
      },
      'zh-TW': {
        common: zhTWCommon,
        home: zhTWHome,
        activities: zhTWActivities,
        activity: zhTWActivity,
        course: zhTWCourse,
        dashboard: zhTWDashboard,
        profiles: zhTWProfiles,
        contact: zhTWContact,
        passRefund: zhTWPassRefund,
        refund: zhTWRefund,
        applyHost: zhTWApplyHost,
        trainingProgram: zhTWTrainingProgram,
        auth: zhTWAuth,
        booking: zhTWBooking,
        pass: zhTWPass,
        giftCard: zhTWGiftCard,
        review: zhTWReview,
        rewards: zhTWRewards,
        hostFollow: zhTWHostFollow,
        picks: zhTWPicks,
        investors: zhTWInvestors,
        waitlist: zhTWWaitlist,
      },
    },
    // A key missing from zh-TW (the parity test should prevent it) falls back to
    // Simplified before English.
    fallbackLng: { 'zh-TW': [LANGUAGES.ZH_CN, LANGUAGES.EN], default: [LANGUAGES.EN] },
    // English plus Chinese in two scripts (docs/prd-bilingual-content.md §5.1).
    supportedLngs: [LANGUAGES.EN, LANGUAGES.ZH_CN, LANGUAGES.ZH_TW],
    load: 'currentOnly',
    defaultNS: 'common', // Default namespace
    ns: [
      'common',
      'home',
      'activities',
      'activity',
      'course',
      'dashboard',
      'profiles',
      'contact',
      'passRefund',
      'refund',
      'terms',
      'applyHost',
      'trainingProgram',
      'auth',
      'booking',
      'pass',
      'review',
      'rewards',
      'hostFollow',
      'picks',
      'investors',
      'waitlist',
    ], // Available namespaces

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    detection: {
      // Detection order and caches
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'yoyoclub-language',
      // Explicit Hans/Hant script wins, then TW/HK/MO region means Traditional,
      // other Chinese is Simplified, everything else English (PRD §5.1).
      convertDetectedLanguage: (lng: string) => normalizeSiteLanguage(lng),
    },

    react: {
      useSuspense: true, // Enable suspense mode
      // Re-render translated components when a script converter finishes loading.
      bindI18n: 'languageChanged scriptLoaded',
    },
  });

// Keep <html lang> in step with the active language (index.html is static),
// and fetch the script converter for host-written Chinese (PRD §5.1).
i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
  void loadScript(lng);
});
onScriptLoaded(() => i18n.emit('scriptLoaded'));
// init() above already resolved the first language before these listeners existed.
if (typeof document !== 'undefined' && i18n.language) {
  document.documentElement.lang = i18n.language;
}
void loadScript(i18n.language);

export default i18n;
